import React, { useState, useEffect, useMemo } from 'react';
import ApiService from '../../../_services/ApiService';
import ConfirmModal from './ConfirmModal';
import AlertModal from './AlertModal';

// Meeting type constants
const ONLINE_MEETING_ZOOM = 44;
const ONLINE_MEETING_TEAMS = 45;
const ONLINE_MEETING_WEBINAR = 46;

interface WeekPatternSlot {
  id?: number;
  dayOfWeek: number; // 1=Sunday, 2=Monday, ..., 7=Saturday
  startTime: string;
  endTime: string;
  instructorId?: number;
  instructorName?: string;
  topicMasterId?: number;
  topicName?: string;
  name?: string;
  agenda?: string;
  batchIds?: number[];
  isOnlineMeeting?: boolean;
  onlineMeetingType?: number | null;
}

interface WeekPatternGridProps {
  templateId: number;
  template: any;
  onGenerate: () => void;
  onImport?: () => void;
}

const WeekPatternGrid: React.FC<WeekPatternGridProps> = ({ templateId, template, onGenerate, onImport }) => {
  const [pattern, setPattern] = useState<WeekPatternSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState('');
  const [generateEndDate, setGenerateEndDate] = useState('');
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<WeekPatternSlot | null>(null);
  const [slotFormData, setSlotFormData] = useState<any>({
    dayOfWeek: 1,
    startTime: '09:00',
    endTime: '10:00',
    instructorId: '',
    instructorName: '',
    topicMasterId: '',
    topicName: '',
    batchIds: [] as number[],
    isOnlineMeeting: false,
    onlineMeetingType: null,
  });

  // Autocomplete states
  const [instructorSearch, setInstructorSearch] = useState('');
  const [topicSearch, setTopicSearch] = useState('');
  const [instructorOptions, setInstructorOptions] = useState<any[]>([]);
  const [topicOptions, setTopicOptions] = useState<any[]>([]);
  const [showInstructorDropdown, setShowInstructorDropdown] = useState(false);
  const [showTopicDropdown, setShowTopicDropdown] = useState(false);
  const [instructorSearchLoading, setInstructorSearchLoading] = useState(false);
  const [topicSearchLoading, setTopicSearchLoading] = useState(false);

  // Linked selection: filtered options when one field is selected
  const [filteredInstructors, setFilteredInstructors] = useState<any[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<any[]>([]);

  // Track which field was selected first (for dynamic field ordering)
  const [firstSelected, setFirstSelected] = useState<'instructor' | 'topic' | null>(null);

  // Modal states
  const [confirmModal, setConfirmModal] = useState<{
    title?: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [alertModal, setAlertModal] = useState<{
    title?: string;
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Derive time rows dynamically from actual pattern data
  const timeSlots = useMemo(() => {
    const uniqueSlots = new Set<string>();
    pattern.forEach(slot => {
      const start = slot.startTime.substring(0, 5);
      const end = slot.endTime.substring(0, 5);
      uniqueSlots.add(`${start}-${end}`);
    });
    return Array.from(uniqueSlots).sort();
  }, [pattern]);

  useEffect(() => {
    fetchPattern();
  }, [templateId]);

  const fetchPattern = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getWeekPattern(templateId);
      if (response.data.success) {
        setPattern(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching week pattern:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = (dayOfWeek: number, timeSlot: string) => {
    const [start, end] = timeSlot.split('-');
    setSlotFormData({
      dayOfWeek,
      startTime: start,
      endTime: end,
      instructorId: '',
      instructorName: '',
      topicMasterId: '',
      topicName: '',
      name: '',
      agenda: '',
      batchIds: [],
      isOnlineMeeting: false,
      onlineMeetingType: null,
    });
    setInstructorSearch('');
    setTopicSearch('');
    setFilteredInstructors([]);
    setFilteredTopics([]);
    setFirstSelected(null);
    setEditingSlot(null);
    setShowSlotModal(true);
  };

  const handleAddNewSlot = () => {
    setSlotFormData({
      dayOfWeek: 2, // Monday
      startTime: '09:00',
      endTime: '10:00',
      instructorId: '',
      instructorName: '',
      topicMasterId: '',
      topicName: '',
      name: '',
      agenda: '',
      batchIds: [],
      isOnlineMeeting: false,
      onlineMeetingType: null,
    });
    setInstructorSearch('');
    setTopicSearch('');
    setFilteredInstructors([]);
    setFilteredTopics([]);
    setFirstSelected(null);
    setEditingSlot(null);
    setShowSlotModal(true);
  };

  const handleEditSlot = async (slot: WeekPatternSlot) => {
    setSlotFormData({
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
      instructorId: slot.instructorId || '',
      instructorName: slot.instructorName || '',
      topicMasterId: slot.topicMasterId || '',
      topicName: slot.topicName || '',
      name: slot.name || '',
      agenda: slot.agenda || '',
      batchIds: slot.batchIds || [],
      isOnlineMeeting: slot.isOnlineMeeting || false,
      onlineMeetingType: slot.onlineMeetingType || null,
    });
    setInstructorSearch(slot.instructorName || '');
    setTopicSearch(slot.topicName || '');
    setFilteredInstructors([]);
    setFilteredTopics([]);
    // When editing, default to instructor-first order (both or instructor filled)
    // Only set topic-first if only topic is filled
    if (slot.topicMasterId && !slot.instructorId) {
      setFirstSelected('topic');
    } else {
      setFirstSelected(null);
    }
    setEditingSlot(slot);
    setShowSlotModal(true);

    // Load filtered options based on existing selections
    if (slot.instructorId) {
      try {
        const response = await ApiService.getTopicsForInstructor(template.id, slot.instructorId, slot.batchIds || []);
        if (response.data.success) setFilteredTopics(response.data.data || []);
      } catch (error) {
        console.error('Error fetching topics for instructor:', error);
      }
    }
    if (slot.topicMasterId) {
      try {
        const response = await ApiService.getInstructorsForTopic(template.id, slot.topicMasterId, slot.batchIds || []);
        if (response.data.success) setFilteredInstructors(response.data.data || []);
      } catch (error) {
        console.error('Error fetching instructors for topic:', error);
      }
    }
  };

  const handleDeleteSlot = (slotId: number) => {
    setConfirmModal({
      title: 'Remove Slot',
      message: 'Remove this slot from the pattern?',
      onConfirm: () => {
        setPattern(prev => prev.filter(s => s.id !== slotId));
        setConfirmModal(null);
      },
    });
  };

  const handleSaveSlot = () => {
    if (!slotFormData.batchIds || slotFormData.batchIds.length === 0) {
      setAlertModal({ message: 'Please select at least one batch.', type: 'error' });
      return;
    }

    const newSlot: WeekPatternSlot = {
      ...editingSlot,
      dayOfWeek: slotFormData.dayOfWeek,
      startTime: slotFormData.startTime,
      endTime: slotFormData.endTime,
      instructorId: slotFormData.instructorId ? parseInt(slotFormData.instructorId) : undefined,
      instructorName: slotFormData.instructorName,
      topicMasterId: slotFormData.topicMasterId ? parseInt(slotFormData.topicMasterId) : undefined,
      topicName: slotFormData.topicName,
      name: slotFormData.name,
      agenda: slotFormData.agenda,
      batchIds: slotFormData.batchIds,
      isOnlineMeeting: slotFormData.isOnlineMeeting,
      onlineMeetingType: slotFormData.isOnlineMeeting ? slotFormData.onlineMeetingType : null,
    };

    if (editingSlot && editingSlot.id) {
      // Update existing
      setPattern(prev => prev.map(s => s.id === editingSlot.id ? { ...newSlot, id: editingSlot.id } : s));
    } else {
      // Add new
      setPattern(prev => [...prev, { ...newSlot, id: Date.now() }]); // Temporary ID
    }

    setShowSlotModal(false);
  };

  const savePatternToServer = async () => {
    const response = await ApiService.saveWeekPattern(templateId, pattern);
    return response.data.success;
  };

  const handleSavePattern = async () => {
    try {
      setLoading(true);
      const success = await savePatternToServer();
      if (success) {
        setAlertModal({ message: 'Week pattern saved successfully!', type: 'success' });
      }
    } catch (error) {
      console.error('Error saving pattern:', error);
      setAlertModal({ message: 'Failed to save pattern', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSlots = () => {
    setGenerateStartDate(template.startDate || '');
    setGenerateEndDate(template.endDate || '');
    setShowGenerateModal(true);
  };

  const confirmGenerateSlots = async () => {
    if (!generateStartDate || !generateEndDate) {
      setAlertModal({ message: 'Please select both start and end dates.', type: 'error' });
      return;
    }
    if (generateStartDate > generateEndDate) {
      setAlertModal({ message: 'Start date must be before end date.', type: 'error' });
      return;
    }
    setShowGenerateModal(false);
    try {
      setLoading(true);
      await savePatternToServer();
      const response = await ApiService.generateSlotsFromPattern(templateId, generateStartDate, generateEndDate);
      if (response.data.success) {
        const { count, existing } = response.data.data;
        const meetingErrors = response.data.meetingErrors || [];

        let message: string;
        if (count > 0) {
          message = `Generated ${count} new slots from pattern!`;

          // Append meeting errors if any
          if (meetingErrors.length > 0) {
            message += `\n\n⚠️ Warning: ${meetingErrors.length} online meeting(s) could not be created:\n`;
            meetingErrors.slice(0, 3).forEach((err: any) => {
              message += `\n• Slot on ${err.date}: ${err.error}`;
            });
            if (meetingErrors.length > 3) {
              message += `\n• ... and ${meetingErrors.length - 3} more`;
            }

            // Check if any error has authUrl
            const firstWithAuth = meetingErrors.find((e: any) => e.authUrl);
            if (firstWithAuth) {
              message += '\n\nPlease re-authorize the meeting provider and regenerate to create meetings.';
            }
          }
        } else if (existing > 0) {
          message = `All ${existing} slots already exist. Nothing new to generate.`;
        } else {
          message = response.data.message || 'No matching days found for the pattern in the date range.';
        }

        setAlertModal({
          message,
          type: count > 0 ? (meetingErrors.length > 0 ? 'info' : 'success') : 'info',
        });
        onGenerate();
      }
    } catch (error: any) {
      console.error('Error generating slots:', error);
      const detail = error.response?.data?.message || '';
      setAlertModal({
        message: (error.response?.data?.error || 'Failed to generate slots') + (detail ? `\n\n${detail}` : ''),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearPattern = () => {
    setConfirmModal({
      title: 'Clear Pattern',
      message: 'Clear the entire week pattern? This cannot be undone.',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setLoading(true);
          await ApiService.saveWeekPattern(templateId, []);
          setPattern([]);
          setAlertModal({ message: 'Pattern cleared successfully!', type: 'success' });
        } catch (error) {
          console.error('Error clearing pattern:', error);
          setAlertModal({ message: 'Failed to clear pattern', type: 'error' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Autocomplete handlers (same as TemplateDetail)
  const handleInstructorSearch = async (query: string) => {
    setInstructorSearch(query);
    if (query.length >= 2 && template) {
      setInstructorSearchLoading(true);
      setShowInstructorDropdown(true);
      try {
        const response = await ApiService.searchInstructorsByTemplate(template.id, query, slotFormData.batchIds);
        if (response.data.success) {
          setInstructorOptions(response.data.data || []);
        }
      } catch (error) {
        console.error('Error searching instructors:', error);
        setInstructorOptions([]);
      } finally {
        setInstructorSearchLoading(false);
      }
    } else {
      setInstructorOptions([]);
      setShowInstructorDropdown(false);
    }
  };

  const handleTopicSearch = async (query: string) => {
    setTopicSearch(query);
    if (query.length >= 2 && template) {
      setTopicSearchLoading(true);
      setShowTopicDropdown(true);
      try {
        const response = await ApiService.searchTopicsByTemplate(template.id, query, slotFormData.batchIds);
        if (response.data.success) {
          setTopicOptions(response.data.data || []);
        }
      } catch (error) {
        console.error('Error searching topics:', error);
        setTopicOptions([]);
      } finally {
        setTopicSearchLoading(false);
      }
    } else {
      setTopicOptions([]);
      setShowTopicDropdown(false);
    }
  };

  const selectInstructor = async (instructor: any) => {
    const name = instructor.name || `${instructor.firstName} ${instructor.lastName}`;
    setSlotFormData((prev: any) => ({
      ...prev,
      instructorId: instructor.id.toString(),
      instructorName: name,
    }));
    setInstructorSearch(name);
    setShowInstructorDropdown(false);

    // Track which field was selected first
    if (!firstSelected) setFirstSelected('instructor');

    // Fetch filtered topics for this instructor
    try {
      const response = await ApiService.getTopicsForInstructor(template.id, instructor.id, slotFormData.batchIds);
      if (response.data.success) {
        setFilteredTopics(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching topics for instructor:', error);
    }
  };

  const clearInstructor = () => {
    setSlotFormData((prev: any) => ({
      ...prev,
      instructorId: '',
      instructorName: '',
    }));
    setInstructorSearch('');
    setFilteredTopics([]);
    if (firstSelected === 'instructor') setFirstSelected(null);
  };

  const selectTopic = async (topic: any) => {
    setSlotFormData((prev: any) => ({
      ...prev,
      topicMasterId: topic.id.toString(),
      topicName: topic.name,
    }));
    setTopicSearch(topic.name);
    setShowTopicDropdown(false);

    // Track which field was selected first
    if (!firstSelected) setFirstSelected('topic');

    // Fetch filtered instructors for this topic
    try {
      const response = await ApiService.getInstructorsForTopic(template.id, topic.id, slotFormData.batchIds);
      if (response.data.success) {
        setFilteredInstructors(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching instructors for topic:', error);
    }
  };

  const clearTopic = () => {
    setSlotFormData((prev: any) => ({
      ...prev,
      topicMasterId: '',
      topicName: '',
    }));
    setTopicSearch('');
    setFilteredInstructors([]);
    if (firstSelected === 'topic') setFirstSelected(null);
  };

  // Toggling a batch invalidates any instructor/topic pick — those options are batch-scoped,
  // so we reset them so the next pick reflects the currently-selected batches.
  const handleBatchToggle = (batchId: number, checked: boolean) => {
    setSlotFormData((prev: any) => ({
      ...prev,
      batchIds: checked
        ? [...prev.batchIds, batchId]
        : prev.batchIds.filter((id: number) => id !== batchId),
      instructorId: '',
      instructorName: '',
      topicMasterId: '',
      topicName: '',
    }));
    setInstructorSearch('');
    setTopicSearch('');
    setInstructorOptions([]);
    setTopicOptions([]);
    setShowInstructorDropdown(false);
    setShowTopicDropdown(false);
    setFilteredInstructors([]);
    setFilteredTopics([]);
    setFirstSelected(null);
  };

  const getSlotsForCell = (dayOfWeek: number, timeSlot: string): WeekPatternSlot[] => {
    const [start, end] = timeSlot.split('-');
    return pattern.filter(
      s => s.dayOfWeek === dayOfWeek && s.startTime.substring(0, 5) === start && s.endTime.substring(0, 5) === end
    );
  };

  if (loading && pattern.length === 0) {
    return <div className="week-pattern-grid">Loading pattern...</div>;
  }

  return (
    <div className="week-pattern-grid">
      <div className="pattern-header">
        <h3>📐 Design Weekly Pattern</h3>
        <p>Create a recurring weekly timetable pattern. Slots will be generated for each week within the template date range.</p>
      </div>

      {timeSlots.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>Time</th>
              {dayNames.map((day, idx) => (
                <th key={idx}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((timeSlot, rowIdx) => (
              <tr key={rowIdx}>
                <td className="time-cell">{timeSlot}</td>
                {dayNames.map((_, dayIdx) => {
                  const dayOfWeek = dayIdx + 1; // 1=Sunday
                  const cellSlots = getSlotsForCell(dayOfWeek, timeSlot);

                  return (
                    <td key={dayIdx}>
                      {cellSlots.map((slot) => (
                        <div key={slot.id} className="pattern-slot-card">
                          <div className="slot-time">{timeSlot}</div>
                          {slot.instructorName && (
                            <div className="slot-instructor">👤 {slot.instructorName}</div>
                          )}
                          {slot.topicName && (
                            <div className="slot-topic">📚 {slot.topicName}</div>
                          )}
                          {slot.batchIds && slot.batchIds.length > 0 && template?.batchMappings && (
                            <div className="slot-batch">
                              🎓 {slot.batchIds.map((bid: number) => {
                                const batch = template.batchMappings.find((b: any) => b.batchId === bid);
                                return batch?.batchName || `Batch ${bid}`;
                              }).join(', ')}
                            </div>
                          )}
                          <div className="slot-actions">
                            <button className="edit" onClick={() => handleEditSlot(slot)}>✏️</button>
                            <button className="delete" onClick={() => slot.id && handleDeleteSlot(slot.id)}>🗑️</button>
                          </div>
                        </div>
                      ))}
                      <button
                        className={`add-slot-button ${cellSlots.length > 0 ? 'compact' : ''}`}
                        onClick={() => handleAddSlot(dayOfWeek, timeSlot)}
                      >
                        + Add
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="empty-pattern">
          <p>No pattern slots yet. Add your first slot to start designing the weekly pattern.</p>
        </div>
      )}

      <button className="add-new-time-slot" onClick={handleAddNewSlot}>
        + Add New Time Slot
      </button>

      <div className="grid-actions">
        {onImport && (
          <button className="import" onClick={onImport} disabled={loading}>
            Import from Excel
          </button>
        )}
        <button className="clear" onClick={handleClearPattern} disabled={loading || pattern.length === 0}>
          Clear Pattern
        </button>
        <button className="save" onClick={handleSavePattern} disabled={loading || pattern.length === 0}>
          Save Pattern
        </button>
        <button className="generate" onClick={handleGenerateSlots} disabled={loading || pattern.length === 0}>
          Generate Slots from Pattern
        </button>
      </div>

      {/* Slot Edit Modal */}
      {showSlotModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="pattern-slot-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="pattern-slot-title">{editingSlot ? 'Edit Pattern Slot' : 'Add Pattern Slot'}</h3>
              <button className="modal-close" onClick={() => setShowSlotModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-field">
                  <label>Day of Week</label>
                  <select
                    value={slotFormData.dayOfWeek}
                    onChange={(e) => setSlotFormData((prev: any) => ({ ...prev, dayOfWeek: parseInt(e.target.value) }))}
                  >
                    {dayNames.map((day, idx) => (
                      <option key={idx} value={idx + 1}>{day}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-field">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={slotFormData.startTime}
                    onChange={(e) => setSlotFormData((prev: any) => ({ ...prev, startTime: e.target.value }))}
                  />
                </div>
                <div className="form-field">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={slotFormData.endTime}
                    onChange={(e) => setSlotFormData((prev: any) => ({ ...prev, endTime: e.target.value }))}
                  />
                </div>
              </div>

              {/* Batch(es) — picking a batch enables Instructor/Topic. Changing the batch
                  selection clears Instructor/Topic so they always reflect the chosen batches. */}
              <div className="form-field">
                <label>Batch(es) <span style={{ color: '#d32f2f' }}>*</span></label>
                {template?.batchMappings && template.batchMappings.length > 0 ? (
                  <div className="batch-tags">
                    {template.batchMappings.map((batch: any) => (
                      <label key={batch.batchId} className="batch-tag">
                        <input
                          type="checkbox"
                          checked={slotFormData.batchIds.includes(batch.batchId)}
                          onChange={(e) => handleBatchToggle(batch.batchId, e.target.checked)}
                        />
                        {batch.batchName || `Batch ${batch.batchId}`}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="no-batches">No batches assigned to this template. Add batches in the template settings first.</div>
                )}
              </div>

              {/* Instructor/Topic fields - disabled until at least one batch is picked. Order swaps based on which was selected first. */}
              {(!slotFormData.batchIds || slotFormData.batchIds.length === 0) ? (
                <>
                  <div className="form-field autocomplete-field">
                    <label>Instructor</label>
                    <input type="text" placeholder="Select a batch first" disabled />
                  </div>
                  <div className="form-field autocomplete-field">
                    <label>Topic</label>
                    <input type="text" placeholder="Select a batch first" disabled />
                  </div>
                </>
              ) : (() => {
                const instructorField = (
                  <div className="form-field autocomplete-field" key="instructor">
                    <label>Instructor</label>
                    {slotFormData.instructorId ? (
                      <div className="selected-value">
                        <span>{slotFormData.instructorName}</span>
                        <button className="clear-btn" onClick={clearInstructor} title="Clear">×</button>
                      </div>
                    ) : filteredInstructors.length > 0 ? (
                      <>
                        <select
                          value=""
                          onChange={(e) => {
                            const instructor = filteredInstructors.find((i: any) => i.id.toString() === e.target.value);
                            if (instructor) selectInstructor(instructor);
                          }}
                        >
                          <option value="">-- Select Instructor --</option>
                          {filteredInstructors.map((instructor: any) => (
                            <option key={instructor.id} value={instructor.id}>
                              {instructor.name || `${instructor.firstName} ${instructor.lastName}`}
                              {instructor.code ? ` (${instructor.code})` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="field-hint">Filtered by selected topic</div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={instructorSearch}
                          onChange={(e) => handleInstructorSearch(e.target.value)}
                          onFocus={() => instructorSearch.length >= 2 && setShowInstructorDropdown(true)}
                          placeholder="Type to search instructor..."
                        />
                        {showInstructorDropdown && (
                          <div className="autocomplete-dropdown" onMouseDown={(e) => e.preventDefault()}>
                            {instructorSearchLoading ? (
                              <div className="autocomplete-item loading">
                                <div className="item-name">Searching...</div>
                              </div>
                            ) : instructorOptions.length > 0 ? (
                              instructorOptions.map((instructor) => (
                                <div
                                  key={instructor.id}
                                  className="autocomplete-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectInstructor(instructor);
                                  }}
                                >
                                  <div className="item-name">
                                    {instructor.name || `${instructor.firstName} ${instructor.lastName}`}
                                  </div>
                                  {instructor.code && (
                                    <div className="item-meta">{instructor.code}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="autocomplete-item no-results">
                                <div className="item-name">No instructors found matching "{instructorSearch}"</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );

                const topicField = (
                  <div className="form-field autocomplete-field" key="topic">
                    <label>Topic</label>
                    {slotFormData.topicMasterId ? (
                      <div className="selected-value">
                        <span>{slotFormData.topicName}</span>
                        <button className="clear-btn" onClick={clearTopic} title="Clear">×</button>
                      </div>
                    ) : filteredTopics.length > 0 ? (
                      <>
                        <select
                          value=""
                          onChange={(e) => {
                            const topic = filteredTopics.find((t: any) => t.id.toString() === e.target.value);
                            if (topic) selectTopic(topic);
                          }}
                        >
                          <option value="">-- Select Topic --</option>
                          {filteredTopics.map((topic: any) => (
                            <option key={topic.id} value={topic.id}>
                              {topic.name}
                              {topic.code ? ` (${topic.code})` : ''}
                            </option>
                          ))}
                        </select>
                        <div className="field-hint">Filtered by selected instructor</div>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={topicSearch}
                          onChange={(e) => handleTopicSearch(e.target.value)}
                          onFocus={() => topicSearch.length >= 2 && setShowTopicDropdown(true)}
                          placeholder="Type to search topic..."
                        />
                        {showTopicDropdown && (
                          <div className="autocomplete-dropdown" onMouseDown={(e) => e.preventDefault()}>
                            {topicSearchLoading ? (
                              <div className="autocomplete-item loading">
                                <div className="item-name">Searching...</div>
                              </div>
                            ) : topicOptions.length > 0 ? (
                              topicOptions.map((topic) => (
                                <div
                                  key={topic.id}
                                  className="autocomplete-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectTopic(topic);
                                  }}
                                >
                                  <div className="item-name">{topic.name}</div>
                                  {topic.code && (
                                    <div className="item-meta">{topic.code}</div>
                                  )}
                                </div>
                              ))
                            ) : (
                              <div className="autocomplete-item no-results">
                                <div className="item-name">No topics found matching "{topicSearch}"</div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );

                return firstSelected === 'topic'
                  ? <>{topicField}{instructorField}</>
                  : <>{instructorField}{topicField}</>;
              })()}

              <div className="form-field">
                <label>Agenda (Optional)</label>
                <textarea
                  value={slotFormData.agenda || ''}
                  onChange={(e) => setSlotFormData((prev: any) => ({ ...prev, agenda: e.target.value }))}
                  placeholder="Enter agenda or notes..."
                />
              </div>

              {/* Online Meeting */}
              <div className="form-field" style={{ marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={slotFormData.isOnlineMeeting || false}
                    onChange={(e) =>
                      setSlotFormData((prev: any) => ({
                        ...prev,
                        isOnlineMeeting: e.target.checked,
                        onlineMeetingType: e.target.checked ? (prev.onlineMeetingType || ONLINE_MEETING_ZOOM) : null,
                      }))
                    }
                    style={{ width: 'auto', marginRight: '0' }}
                  />
                  <span>Create Online Meeting</span>
                </label>
              </div>

              {slotFormData.isOnlineMeeting && (
                <div className="form-field">
                  <label>Meeting Type</label>
                  <select
                    value={slotFormData.onlineMeetingType || ONLINE_MEETING_ZOOM}
                    onChange={(e) =>
                      setSlotFormData((prev: any) => ({
                        ...prev,
                        onlineMeetingType: parseInt(e.target.value, 10),
                      }))
                    }
                  >
                    <option value={ONLINE_MEETING_ZOOM}>Zoom Meeting</option>
                    <option value={ONLINE_MEETING_TEAMS}>Teams Meeting</option>
                    <option value={ONLINE_MEETING_WEBINAR}>Zoom Webinar</option>
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowSlotModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSaveSlot}>Save Slot</button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Slots Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" role="presentation">
          <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="generate-slots-title" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="generate-slots-title">Generate Slots from Pattern</h3>
              <button className="modal-close" onClick={() => setShowGenerateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#616161', fontSize: '14px' }}>
                Select the date range to generate slots. Existing slots will be kept — only missing slots will be added.
              </p>
              <div className="form-row">
                <div className="form-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={generateStartDate}
                    onChange={(e) => setGenerateStartDate(e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={generateEndDate}
                    onChange={(e) => setGenerateEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowGenerateModal(false)}>Cancel</button>
              <button className="btn-save" onClick={confirmGenerateSlots}>Generate Slots</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {/* Alert Modal */}
      {alertModal && (
        <AlertModal
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal(null)}
        />
      )}
    </div>
  );
};

export default WeekPatternGrid;
